/**
 * WorkshopList — the shared card list of workshops.
 *
 * A port of the design system's `components/WorkshopList/WorkshopList.jsx`,
 * and the presentation the design uses wherever workshops are read on a
 * narrow screen. Shared deliberately: the mobile Overview, and the surfaces
 * that follow it, must agree on what a workshop looks like in a list.
 *
 * Keeps taking our own data — `workshops: WorkshopChip[]`. The design decides
 * the UI (the eleven visibility switches below, the size scale,
 * `right`/`style`/`background`); it does not decide our data shape, so there
 * is no `items` + lookup-table contract here.
 *
 * Where this port differs from the design, and why:
 *
 *  - The click affordance. The design puts `onClick` on the row itself; we
 *    leave the card inert and lay a real button over it, so that a caller's
 *    `right` controls are not nested inside something announced as a button.
 *    This is the largest change to the row's composition.
 *  - No `theme` prop. We theme through CSS variables on a root class, and a
 *    component-level prop would be a second mechanism that can disagree with
 *    the first one on the same page.
 *  - The empty list is a card saying so, which the design does not draw at
 *    all. At this width the page suppresses its own empty state, on the
 *    grounds that the list speaks to its own emptiness.
 *  - The type scale lives in `workshopListSizes`, and the date block's own
 *    scale in `DateBadge` — see those two files.
 *  - Pacing renders the status the server sends rather than the design's
 *    thresholds, and is opt-in — see `workshopListVisibility`.
 *  - The sales row is not pinned to the card's bottom — see
 *    `WorkshopListSalesRow`.
 *  - The tree seal shows a count and this repo's tree glyph. The design also
 *    shows the replacement species, which our data does not carry, and its
 *    own icon module was never synced down.
 *  - The tag names the viewer's part on the crew, as the design does, and adds
 *    "Training" for a teacher in training. A row with no role says "Yours".
 *
 * Two colour differences are recorded rather than fixed, because both
 * predate this work and changing them would move the live page further than
 * this change is meant to reach:
 *
 *  - The time pill is filled with `--nb-primary-soft`; the design fills it
 *    with `--leaf`.
 *  - The sales bar uses the app's own bar tokens; the design uses its forest
 *    and clay.
 *
 * The calendar card's List view is this same list (`WorkshopsCalendarListView`),
 * so there is one workshop-list presentation, not two.
 *
 * Sorting is the caller's business; the list renders what it is handed.
 */
import type { CSSProperties, ReactElement, ReactNode } from 'react';

import type { WorkshopListDateField } from './WorkshopListDateBlock';
import { WorkshopListItem } from './WorkshopListItem';
import type { WorkshopListSize } from './workshopListSizes';
import { WORKSHOP_LIST_SIZES } from './workshopListSizes';
import type { WorkshopListSwitches } from './workshopListVisibility';
import type { WorkshopChip, WorkshopClickHandler } from './workshopsCalendarTypes';

export interface WorkshopListProps extends WorkshopListSwitches {
  workshops: WorkshopChip[];
  onWorkshopClick?: WorkshopClickHandler;
  /** Shown in place of the list when there is nothing to show. */
  emptyLabel?: string;
  /** Which of the date block's three lines render. Default all three. */
  dateFields?: WorkshopListDateField[];
  /** Row scale for the three target viewports. Default "md". */
  size?: WorkshopListSize;
  /** Custom right-side renderer; replaces a row's sales row. */
  right?: (w: WorkshopChip) => ReactNode;
  /** Gates which rows open on click — a row for which this returns `false`
   *  renders with no open button at all, regardless of `onWorkshopClick`.
   *  Omit for every row to stay clickable (default). */
  canOpen?: (w: WorkshopChip) => boolean;
  /** Extra inline styles merged onto the list's own wrapper. Not applied to
   *  the empty state, which is a single card rather than a column of them —
   *  a `gap` or `flexDirection` meant for the list means nothing there. */
  style?: CSSProperties;
  /** Overrides every card's background, to blend rows into their container. */
  background?: string;
}

export function WorkshopList({
  workshops,
  onWorkshopClick,
  emptyLabel = 'No workshops scheduled in this period.',
  dateFields,
  size = 'md',
  right,
  canOpen,
  style,
  background,
  ...switches
}: WorkshopListProps): ReactElement {
  if (workshops.length === 0) {
    return (
      <p
        style={{
          margin: 0,
          padding: 14,
          borderRadius: 10,
          background: background ?? 'var(--nb-workshop-card)',
          border: '1px dashed var(--nb-border)',
          fontSize: 12.5,
          color: 'var(--nb-subtle)',
        }}
      >
        {emptyLabel}
      </p>
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: WORKSHOP_LIST_SIZES[size].rowGap,
        ...style,
      }}
    >
      {workshops.map((w) => (
        <WorkshopListItem
          key={w.id}
          w={w}
          onWorkshopClick={canOpen && !canOpen(w) ? undefined : onWorkshopClick}
          dateFields={dateFields}
          size={size}
          right={right}
          background={background}
          {...switches}
        />
      ))}
    </div>
  );
}
